/**
 * Step 1: select which of The Pines' three venue spaces this wedding uses.
 * Cards, not a dropdown — PRD §14.3's "feels guided" emphasis means every
 * choice should be a tappable card with a visual, not a form control.
 */

import { useRef } from 'react';
import { useRoomDefinitions } from '../../api/roomDefinitions';
import type { Room } from '../../domain/types';
import type { WizardData } from './NewWeddingWizard';

interface StepVenueSelectProps {
  data: WizardData;
  onChange: (changes: Partial<WizardData>) => void;
  onNext: () => void;
}

export function StepVenueSelect({ data, onChange, onNext }: StepVenueSelectProps) {
  const { data: rooms, isLoading } = useRoomDefinitions();
  // Guards against a double-click (or two fast taps) on a venue card
  // advancing the wizard two steps at once and silently skipping the
  // guest-count step — each render of this component should only ever
  // be allowed to call onNext() once, no matter how many clicks land on
  // it before React unmounts this step.
  const hasAdvancedRef = useRef(false);

  function handleSelect(room: Room) {
    onChange({ room });
    if (hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    // Auto-advance immediately: this is a single, simple choice with no
    // follow-up information to read, so requiring a separate "Continue"
    // click here is pure friction with no benefit. (Compare to the style
    // step, which shows a description on selection and waits briefly
    // before advancing, so the coordinator has a moment to read it.)
    onNext();
  }

  return (
    <div>
      <h2 className="text-center font-display text-2xl text-ink-700">
        Which space are you using?
      </h2>

      {isLoading && <p className="mt-6 text-center font-body text-sm text-ink-400">{'Loading spaces\u2026'}</p>}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {rooms?.map((room) => {
          const selected = data.room === room.room;
          return (
            <button
              key={room.room}
              onClick={() => handleSelect(room.room)}
              className={
                selected
                  ? 'rounded-lg border-2 border-gold-400 bg-gold-50 p-4 text-center transition-colors'
                  : 'rounded-lg border-2 border-ivory-300 bg-white p-4 text-center transition-colors hover:border-gold-300'
              }
            >
              <div className="mx-auto mb-3 h-20 w-full rounded bg-evergreen-100" aria-hidden />
              <p className="font-display text-base text-ink-700">{room.displayName}</p>
              <p className="font-body text-xs text-ink-400">
                {room.widthFt}&apos; {'\u00d7'} {room.lengthFt}&apos;
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
